/**
 * QuestionControlPanel - Controls for question results, answers, and navigation
 */
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Results Control Button Row
 */
const ResultsControls = ({ 
  showResults, 
  showCorrectAnswer, 
  answersLocked, 
  onShowResults, 
  onLockAnswers, 
  onRevealAnswer 
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-4 shadow border border-gray-200 dark:border-gray-700">
    <div className="grid grid-cols-3 gap-4">
      <button
        onClick={onShowResults}
        className={`h-14 px-4 rounded-lg font-semibold text-lg theme-transition flex items-center justify-center gap-2 text-white ${
          showResults ? 'bg-warning-600 hover:bg-warning-700' : 'bg-primary-600 hover:bg-primary-700'
        }`}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        {showResults ? 'Ergebnisse ausblenden' : 'Ergebnisse anzeigen'}
      </button>

      <button
        onClick={onLockAnswers}
        disabled={showCorrectAnswer}
        className={`h-14 px-4 rounded-lg font-semibold text-lg theme-transition flex items-center justify-center gap-2 text-white ${
          answersLocked 
            ? 'bg-danger-600 hover:bg-danger-700' 
            : 'bg-gray-700 hover:bg-gray-600'
        } ${showCorrectAnswer ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {answersLocked ? '🔒 Antworten gesperrt' : '🔓 Antworten sperren'}
      </button>

      <button
        onClick={onRevealAnswer}
        className={`h-14 px-4 rounded-lg font-semibold text-lg theme-transition flex items-center justify-center gap-2 text-white ${
          showCorrectAnswer ? 'bg-success-600 hover:bg-success-700' : 'bg-success-500 hover:bg-success-600'
        }`}
      >
        ✓ {showCorrectAnswer ? 'Richtige ausblenden' : 'Richtige anzeigen'}
      </button>
    </div>
  </div>
);

ResultsControls.propTypes = {
  showResults: PropTypes.bool.isRequired,
  showCorrectAnswer: PropTypes.bool.isRequired,
  answersLocked: PropTypes.bool.isRequired,
  onShowResults: PropTypes.func.isRequired,
  onLockAnswers: PropTypes.func.isRequired,
  onRevealAnswer: PropTypes.func.isRequired
};

/**
 * Navigation Button
 */
const NavButton = ({ onClick, disabled, children, title, wide = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`h-12 ${wide ? 'px-6' : 'px-4'} bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold theme-transition flex items-center gap-2`}
  >
    {children}
  </button>
);

NavButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  wide: PropTypes.bool
};

/**
 * Navigation Controls Row
 */
const NavigationControls = ({ 
  currentIndex, 
  totalQuestions, 
  onFirst, 
  onPrevious, 
  onNext, 
  onLast,
  onBackToLobby 
}) => {
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalQuestions - 1;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-4 shadow border border-gray-200 dark:border-gray-700">
      <div className="flex gap-3 justify-center">
        {/* Back to Lobby button - only shown at first question */}
        {isFirst && onBackToLobby && (
          <NavButton onClick={onBackToLobby} title="Zurück zur Lobby">
            🏠 Lobby
          </NavButton>
        )}
        
        <NavButton onClick={onFirst} disabled={isFirst} title="Erste Frage (↑)">
          ⏮
        </NavButton>
        
        <NavButton onClick={onPrevious} disabled={isFirst} wide>
          ← Zurück
        </NavButton>

        <NavButton onClick={onNext} disabled={isLast} wide>
          Weiter →
        </NavButton>
        
        <NavButton onClick={onLast} disabled={isLast} title="Letzte Frage (↓)">
          ⏭
        </NavButton>
      </div>
    </div>
  );
};

NavigationControls.propTypes = {
  currentIndex: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  onFirst: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onLast: PropTypes.func.isRequired,
  onBackToLobby: PropTypes.func
};

/**
 * Admin Features Row (Leaderboard, Statistics, Export)
 */
const AdminControls = ({ 
  showLeaderboard, 
  showStatistics, 
  onToggleLeaderboard, 
  onToggleStatistics, 
  onExportQuestions, 
  onExportResults,
  hasResults 
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-4 shadow border border-gray-200 dark:border-gray-700">
    <div className="grid grid-cols-4 gap-3">
      <button
        onClick={onToggleLeaderboard}
        className={`h-12 px-4 rounded-lg font-medium theme-transition flex items-center justify-center gap-2 ${
          showLeaderboard ? 'bg-warning-600 hover:bg-warning-700' : 'bg-warning-500/20 text-warning-400 hover:bg-warning-500/30'
        }`}
      >
        🏆 Rangliste
      </button>

      <button
        onClick={onToggleStatistics}
        className={`h-12 px-4 rounded-lg font-medium theme-transition flex items-center justify-center gap-2 ${
          showStatistics ? 'bg-teal-600 hover:bg-teal-700' : 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30'
        }`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        Statistiken
      </button>

      <button
        onClick={onExportQuestions}
        className="h-12 px-4 bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 rounded-lg font-medium theme-transition flex items-center justify-center gap-2"
      >
        📤 Fragen exportieren
      </button>

      <button
        onClick={onExportResults}
        disabled={!hasResults}
        className="h-12 px-4 bg-secondary-500/20 text-secondary-400 hover:bg-secondary-500/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium theme-transition flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        Ergebnisse exportieren
      </button>
    </div>
  </div>
);

AdminControls.propTypes = {
  showLeaderboard: PropTypes.bool.isRequired,
  showStatistics: PropTypes.bool.isRequired,
  onToggleLeaderboard: PropTypes.func.isRequired,
  onToggleStatistics: PropTypes.func.isRequired,
  onExportQuestions: PropTypes.func.isRequired,
  onExportResults: PropTypes.func.isRequired,
  hasResults: PropTypes.bool.isRequired
};

/**
 * Keyboard Shortcuts Help Bar
 */
const KeyboardShortcutsBar = () => (
  <div className="p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
    <div className="text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-3">
      <span className="font-semibold text-gray-900 dark:text-white">⌨️</span>
      <span><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">←→</kbd> Navigation</span>
      <span><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Space</kbd> Ergebnisse</span>
      <span><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+R</kbd> Richtige</span>
      <span><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+L</kbd> Sperren</span>
    </div>
  </div>
);

/**
 * Main QuestionControlPanel Component - Combines all control sections
 */
function QuestionControlPanel({
  // Results state
  showResults,
  showCorrectAnswer,
  answersLocked,
  onShowResults,
  onLockAnswers,
  onRevealAnswer,
  // Navigation
  currentQuestionIndex,
  totalQuestions,
  onFirstQuestion,
  onPreviousQuestion,
  onNextQuestion,
  onLastQuestion,
  onBackToLobby,
  // Admin features
  showLeaderboard,
  showStatistics,
  onToggleLeaderboard,
  onToggleStatistics,
  onExportQuestions,
  onExportResults,
  hasResults,
  // Options
  showKeyboardShortcuts = true
}) {
  return (
    <>
      {/* Keyboard Shortcuts */}
      {showKeyboardShortcuts && <KeyboardShortcutsBar />}

      {/* Results Controls */}
      <ResultsControls
        showResults={showResults}
        showCorrectAnswer={showCorrectAnswer}
        answersLocked={answersLocked}
        onShowResults={onShowResults}
        onLockAnswers={onLockAnswers}
        onRevealAnswer={onRevealAnswer}
      />

      {/* Navigation Controls */}
      <NavigationControls
        currentIndex={currentQuestionIndex}
        totalQuestions={totalQuestions}
        onFirst={onFirstQuestion}
        onPrevious={onPreviousQuestion}
        onNext={onNextQuestion}
        onLast={onLastQuestion}
        onBackToLobby={onBackToLobby}
      />

      {/* Admin Controls */}
      <AdminControls
        showLeaderboard={showLeaderboard}
        showStatistics={showStatistics}
        onToggleLeaderboard={onToggleLeaderboard}
        onToggleStatistics={onToggleStatistics}
        onExportQuestions={onExportQuestions}
        onExportResults={onExportResults}
        hasResults={hasResults}
      />
    </>
  );
}

QuestionControlPanel.propTypes = {
  // Results state
  showResults: PropTypes.bool.isRequired,
  showCorrectAnswer: PropTypes.bool.isRequired,
  answersLocked: PropTypes.bool.isRequired,
  onShowResults: PropTypes.func.isRequired,
  onLockAnswers: PropTypes.func.isRequired,
  onRevealAnswer: PropTypes.func.isRequired,
  // Navigation
  currentQuestionIndex: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  onFirstQuestion: PropTypes.func.isRequired,
  onPreviousQuestion: PropTypes.func.isRequired,
  onNextQuestion: PropTypes.func.isRequired,
  onLastQuestion: PropTypes.func.isRequired,
  onBackToLobby: PropTypes.func,
  // Admin features
  showLeaderboard: PropTypes.bool.isRequired,
  showStatistics: PropTypes.bool.isRequired,
  onToggleLeaderboard: PropTypes.func.isRequired,
  onToggleStatistics: PropTypes.func.isRequired,
  onExportQuestions: PropTypes.func.isRequired,
  onExportResults: PropTypes.func.isRequired,
  hasResults: PropTypes.bool.isRequired,
  // Options
  showKeyboardShortcuts: PropTypes.bool
};

export { ResultsControls, NavigationControls, AdminControls, KeyboardShortcutsBar };
export default QuestionControlPanel;
